import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';

export function useSubscriptionStatus() {
  const [subscriptionData, setSubscriptionData] = useState({
    isSubscribed: false,
    isLoading: true,
    error: null,
    subscriptionStatus: null,
    isPro: false,
    isTrialing: false,
    trialEnd: null,
    subscriptionId: null,
    currentPeriodEnd: null,
    lastChecked: null
  });

  const checkSubscriptionStatus = useCallback(async (userId) => {
    if (!userId) {
      setSubscriptionData(prev => ({
        ...prev,
        isSubscribed: false,
        isLoading: false,
        error: null,
        subscriptionStatus: 'no_user'
      }));
      return;
    }

    try {
      setSubscriptionData(prev => ({ ...prev, isLoading: true, error: null }));

      // Check Firestore for cached subscription data
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        setSubscriptionData(prev => ({
          ...prev,
          isSubscribed: false,
          isLoading: false,
          subscriptionStatus: 'no_profile',
          lastChecked: new Date().toISOString()
        }));
        return;
      }

      const userData = userDoc.data();
      const {
        isPro = false,
        isTrialing = false,
        subscriptionStatus = 'none',
        subscriptionId = null,
        trialEnd = null,
        currentPeriodEnd = null,
        stripeCustomerId = null
      } = userData;

      // Determine if user is subscribed based on multiple factors
      const hasActiveSubscription = isPro || isTrialing;
      const hasValidStatus = ['active', 'trialing'].includes(subscriptionStatus);
      const isSubscribed = hasActiveSubscription && hasValidStatus;

      // Check if trial has expired
      let trialExpired = false;
      if (isTrialing && trialEnd) {
        const trialEndDate = new Date(trialEnd * 1000); // Convert from Unix timestamp
        trialExpired = trialEndDate < new Date();
      }

      // Check if subscription has expired
      let subscriptionExpired = false;
      if (currentPeriodEnd) {
        const periodEndDate = new Date(currentPeriodEnd * 1000);
        subscriptionExpired = periodEndDate < new Date();
      }

      // Final subscription status
      const finalIsSubscribed = isSubscribed && !trialExpired && !subscriptionExpired;

      setSubscriptionData({
        isSubscribed: finalIsSubscribed,
        isLoading: false,
        error: null,
        subscriptionStatus,
        isPro,
        isTrialing: isTrialing && !trialExpired,
        trialEnd,
        subscriptionId,
        currentPeriodEnd,
        stripeCustomerId,
        trialExpired,
        subscriptionExpired,
        lastChecked: new Date().toISOString()
      });

    } catch (error) {
      console.error('Error checking subscription status:', error);
      setSubscriptionData(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
        lastChecked: new Date().toISOString()
      }));
    }
  }, []);

  const refreshSubscriptionStatus = useCallback(() => {
    const user = auth.currentUser;
    if (user) {
      checkSubscriptionStatus(user.uid);
    }
  }, [checkSubscriptionStatus]);

  // Sync subscription status with backend
  const syncWithStripe = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      setSubscriptionData(prev => ({ ...prev, isLoading: true }));
      
      const response = await fetch(`https://klorah-fast-server-my8l.onrender.com/api/sync-subscription-status/${user.uid}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        // After syncing, refresh local data
        await checkSubscriptionStatus(user.uid);
      } else {
        throw new Error('Failed to sync with Stripe');
      }
    } catch (error) {
      console.error('Error syncing with Stripe:', error);
      setSubscriptionData(prev => ({
        ...prev,
        error: 'Failed to sync subscription status',
        isLoading: false
      }));
    }
  }, [checkSubscriptionStatus]);

  // Set up real-time subscription monitoring
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setSubscriptionData(prev => ({
        ...prev,
        isSubscribed: false,
        isLoading: false,
        subscriptionStatus: 'no_user'
      }));
      return;
    }

    // Set up real-time listener for user document changes
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        const {
          isPro = false,
          isTrialing = false,
          subscriptionStatus = 'none',
          subscriptionId = null,
          trialEnd = null,
          currentPeriodEnd = null,
          stripeCustomerId = null
        } = userData;

        // Determine if user is subscribed
        const hasActiveSubscription = isPro || isTrialing;
        const hasValidStatus = ['active', 'trialing'].includes(subscriptionStatus);
        const isSubscribed = hasActiveSubscription && hasValidStatus;

        // Check if trial has expired
        let trialExpired = false;
        if (isTrialing && trialEnd) {
          const trialEndDate = new Date(trialEnd * 1000);
          trialExpired = trialEndDate < new Date();
        }

        // Check if subscription has expired
        let subscriptionExpired = false;
        if (currentPeriodEnd) {
          const periodEndDate = new Date(currentPeriodEnd * 1000);
          subscriptionExpired = periodEndDate < new Date();
        }

        // Final subscription status
        const finalIsSubscribed = isSubscribed && !trialExpired && !subscriptionExpired;

        setSubscriptionData({
          isSubscribed: finalIsSubscribed,
          isLoading: false,
          error: null,
          subscriptionStatus,
          isPro,
          isTrialing: isTrialing && !trialExpired,
          trialEnd,
          subscriptionId,
          currentPeriodEnd,
          stripeCustomerId,
          trialExpired,
          subscriptionExpired,
          lastChecked: new Date().toISOString()
        });
      } else {
        setSubscriptionData(prev => ({
          ...prev,
          isSubscribed: false,
          isLoading: false,
          subscriptionStatus: 'no_profile',
          lastChecked: new Date().toISOString()
        }));
      }
    }, (error) => {
      console.error('Error in subscription status listener:', error);
      setSubscriptionData(prev => ({
        ...prev,
        error: error.message,
        isLoading: false
      }));
    });

    return unsubscribe;
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        setSubscriptionData(prev => ({
          ...prev,
          isSubscribed: false,
          isLoading: false,
          subscriptionStatus: 'no_user'
        }));
      }
      // Real-time listener will handle the case when user exists
    });

    return unsubscribe;
  }, []);

  return {
    ...subscriptionData,
    refreshSubscriptionStatus,
    syncWithStripe
  };
} 